/**
 * Deep link into the Quo (formerly OpenPhone) app's message composer for the driver's number. The message
 * stays on the system clipboard for the operator to paste; it is never sent in the deep link.
 *
 * Scheme reality: Quo's official deep-linking doc still specifies the legacy `openphone://` scheme, while
 * newer builds (and atrek's dispatch) use `quo://`. Which one a given machine has registered depends on the
 * installed app version — so we try `openphone://` first and, if the page never lost focus (nothing launched),
 * retry with `quo://`; if that also goes nowhere, report unavailability so the caller can toast a
 * hint. Pure protocol links — no API, nothing breaks without the app.
 */

/** Best-effort E.164: keep an existing +, else assume US (10 digits → +1XXXXXXXXXX, 11 with leading 1 → +…). */
const normalizePhone = (raw: string): string => {
    const trimmed = raw.trim()
    const digits = trimmed.replace(/[^0-9]/g, '')
    if (trimmed.startsWith('+')) return `+${digits}`
    if (digits.length === 10) return `+1${digits}`
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`
    return digits ? `+${digits}` : ''
}

const buildMessageUrl = (scheme: 'quo' | 'openphone', phone: string): string => {
    const number = normalizePhone(phone)
    return `${scheme}://message?number=${encodeURIComponent(number)}`
}

/** How long we give the OS to launch the app (a successful launch blurs/hides the page) before trying the
 *  other scheme / giving up. Generous enough to cover Chrome's "Open app?" confirm appearing. */
const LAUNCH_WAIT_MS = 1200

/**
 * Open the Quo composer for `phone` without pre-filling a message. Tries the DOCUMENTED `openphone://` scheme
 * first — Quo's deep-linking doc specifies `openphone://message?number=&text=` — then falls back to
 * `quo://`; calls `onUnavailable` when neither scheme appears to be handled.
 *
 * Message text is copied separately by the button. Keeping it out of this function prevents deep-link
 * changes from silently putting a message into the wrong conversation.
 */
export const openInQuo = (phone: string, onUnavailable?: () => void): void => {
    let launched = false
    const markLaunched = () => {
        launched = true
    }
    // A successful protocol launch takes focus away (app switch) or at least fires blur for the browser's
    // own "Open in app?" dialog; an unregistered scheme does neither (Chrome just logs a console error).
    window.addEventListener('blur', markLaunched)
    document.addEventListener('visibilitychange', markLaunched)
    const cleanup = () => {
        window.removeEventListener('blur', markLaunched)
        document.removeEventListener('visibilitychange', markLaunched)
    }
    window.location.href = buildMessageUrl('openphone', phone)
    window.setTimeout(() => {
        if (launched || document.hidden) {
            cleanup()
            return
        }
        // openphone:// went nowhere — this machine likely has a build registering only the new scheme.
        window.location.href = buildMessageUrl('quo', phone)
        window.setTimeout(() => {
            cleanup()
            if (!launched && !document.hidden) onUnavailable?.()
        }, LAUNCH_WAIT_MS)
    }, LAUNCH_WAIT_MS)
}
